import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { Box, Button, Chip, Grid2, Paper, Stack, Typography } from '@mui/material';
import { ArrowBack, LockClock } from '@mui/icons-material';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PercentIcon from '@mui/icons-material/Percent';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ListAltIcon from '@mui/icons-material/ListAlt';
import SaveIcon from '@mui/icons-material/Save';
import { EditableConditionsList } from 'components/shared/EditableConditionsList';
import { CreditSubmission } from '@trnsact/trnsact-shared-types/src/generated';
import { UPDATE_PARSED_DECISION } from '../queries';
import { ApprovalDetailsPanel } from './ApprovalDetailsPanel';
import { ApprovalFieldDef, ApprovalTermFieldStatus } from 'modules/creditSubmission/submissionDetails/types';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ReviewExtractedTermsProps {
  creditSubmission: CreditSubmission;
  parsedDecisionId: string;
  onBack: VoidFunction;
  onSaveComplete?: VoidFunction;
}

// Summary card config
interface SummaryCard {
  label: string;
  key: string;
  icon: React.ReactNode;
  format: (v: any) => string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(v: number | null | undefined): string {
  if (v == null) return '—';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatCurrencyShort(v: number | null | undefined): string {
  if (v == null) return '—';
  return `$${Number(v).toLocaleString('en-US')}`;
}

function formatPercentage(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${v}%`;
}

function formatMonths(v: number | null | undefined): string {
  if (v == null) return '—';
  return `${v} mo`;
}

function toNumber(v: string): number | null {
  const n = parseFloat(v);
  return isNaN(n) ? null : n;
}

function toInt(v: string): number | null {
  const n = parseInt(v, 10);
  return isNaN(n) ? null : n;
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const ReviewExtractedTerms: React.FC<ReviewExtractedTermsProps> = ({
  creditSubmission,
  parsedDecisionId,
  onBack,
  onSaveComplete,
}) => {
  const parsed = creditSubmission.creditDecisionParsedData?.parsedData;
  const lenderName = creditSubmission.lenderName ?? 'Unknown Lender';

  const [updateParsedDecision, { loading: saving }] = useMutation(UPDATE_PARSED_DECISION, {
    refetchQueries: ['GetApprovalsByVendorOpportunityId'],
  });

  // Build initial values from parsed data
  const initialValues = useMemo<Record<string, string>>(() => {
    if (!parsed) return {} as Record<string, string>;
    const docFeeTotal = (parsed.lenderFees ?? [])
      .filter((f: any) => f?.feeName?.toLowerCase()?.includes('doc'))
      .reduce((sum: number, f: any) => sum + (f?.amount ?? 0), 0);
    return {
      lenderName: lenderName,
      decision: creditSubmission.decision ?? parsed.decisionType ?? '',
      applicantName: parsed.applicantName ?? '',
      approvalAmount: parsed.approvalAmount != null ? String(parsed.approvalAmount) : '',
      termLengthMonths: parsed.termLengthMonths != null ? String(parsed.termLengthMonths) : '',
      monthlyPayment: parsed.monthlyPayment != null ? String(parsed.monthlyPayment) : '',
      interestRate: parsed.interestRate != null ? String(parsed.interestRate) : '',
      downPaymentToVendor: parsed.downPaymentToVendor != null ? String(parsed.downPaymentToVendor) : '',
      docFees: docFeeTotal > 0 ? String(docFeeTotal) : '',
      expirationDate: parsed.expirationDate ?? '',
      minimumRequiredDownPayment:
        parsed.minimumRequiredDownPayment != null ? String(parsed.minimumRequiredDownPayment) : '',
      daysToFirstPayment: parsed.daysToFirstPayment != null ? String(parsed.daysToFirstPayment) : '',
      // Rates & Structure
      apr: parsed.apr != null ? String(parsed.apr) : '',
      dealerBuyRate: parsed.dealerBuyRate != null ? String(parsed.dealerBuyRate) : '',
      customerRate: parsed.customerRate != null ? String(parsed.customerRate) : '',
      residualAmount: parsed.residualAmount != null ? String(parsed.residualAmount) : '',
      balloonPayment: parsed.balloonPayment != null ? String(parsed.balloonPayment) : '',
      totalDueAtSigning: parsed.totalDueAtSigning != null ? String(parsed.totalDueAtSigning) : '',
      advancePaymentsCount: parsed.advancePaymentsCount != null ? String(parsed.advancePaymentsCount) : '',
      paymentCalculationType: parsed.paymentCalculationType ?? '',
      // Deal Details
      typeOfFinancing: parsed.typeOfFinancing ?? '',
      programName: parsed.programName ?? '',
      invoiceAmount: parsed.invoiceAmount != null ? String(parsed.invoiceAmount) : '',
      salesPrice: parsed.salesPrice != null ? String(parsed.salesPrice) : '',
      cashDown: parsed.cashDown != null ? String(parsed.cashDown) : '',
      netTradeIn: parsed.netTradeIn != null ? String(parsed.netTradeIn) : '',
      taxesAndFees: parsed.taxesAndFees != null ? String(parsed.taxesAndFees) : '',
      dealerCommission: parsed.dealerCommission != null ? String(parsed.dealerCommission) : '',
      // Parties
      underwriterName: parsed.underwriterName ?? '',
      coBorrowerName: parsed.coBorrowerName ?? '',
      dealerName: parsed.dealerName ?? '',
      guarantorName: parsed.guarantorName ?? '',
      // Program & Terms
      irregularPayments: parsed.irregularPayments ?? '',
      // Details (collapsed)
      recourseType: '',
      // Dates
      decisionDate: parsed.decisionDate ?? '',
      firstPaymentDueDate: parsed.firstPaymentDueDate ?? '',
    };
  }, [parsed, lenderName, creditSubmission.decision]);

  const [form, setForm] = useState<Record<string, string>>(initialValues);
  const [conditions, setConditions] = useState<string[]>([]);

  // Sync from data
  useEffect(() => {
    setForm(initialValues);
    const merged = [...(parsed?.conditions ?? []), ...(parsed?.stipulations ?? [])].filter(
      (c: any): c is string => c != null && c.trim() !== ''
    );
    setConditions(merged);
  }, [initialValues, parsed]);

  const originalValues = initialValues;

  const getFieldStatus = useCallback(
    (key: string): ApprovalTermFieldStatus => {
      const current = form[key] ?? '';
      const original = originalValues[key] ?? '';
      if (!current && !original) return 'empty';
      if (current !== original) return 'edited';
      return current ? 'extracted' : 'empty';
    },
    [form, originalValues]
  );

  const getDisplayValue = useCallback(
    (key: string, fDef: ApprovalFieldDef): string => {
      const val = form[key] ?? '';
      if (!val) return '';
      const num = parseFloat(val);
      switch (fDef.type) {
        case 'currency':
          return isNaN(num) ? val : formatCurrency(num);
        case 'percentage':
          return isNaN(num) ? val : formatPercentage(num);
        case 'date':
          return val;
        default:
          return val;
      }
    },
    [form]
  );

  const handleFieldChange = (key: string) => (val: string) => {
    setForm(prev => ({ ...prev, [key]: val }));
  };

  // Conditions management
  const handleRemoveCondition = (index: number) => {
    setConditions(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddCondition = () => {
    setConditions(prev => [...prev, '']);
  };

  const handleConditionChange = (index: number, value: string) => {
    setConditions(prev => prev.map((c, i) => (i === index ? value : c)));
  };

  // Save handler
  const handleSaveAll = async () => {
    const lenderFees = parsed?.lenderFees ?? [];
    const termOptions = parsed?.termOptions ?? [];
    const paymentTiers = parsed?.paymentTiers ?? [];

    const parsedData: Record<string, any> = {
      decisionType: form.decision || null,
      approvalAmount: toNumber(form.approvalAmount ?? ''),
      termLengthMonths: toInt(form.termLengthMonths ?? ''),
      interestRate: toNumber(form.interestRate ?? ''),
      monthlyPayment: toNumber(form.monthlyPayment ?? ''),
      downPaymentToVendor: toNumber(form.downPaymentToVendor ?? ''),
      minimumRequiredDownPayment: toNumber(form.minimumRequiredDownPayment ?? ''),
      daysToFirstPayment: toInt(form.daysToFirstPayment ?? ''),
      applicantName: form.applicantName || null,
      businessName: parsed?.businessName || null,
      conditions: conditions.filter(c => c.trim() !== ''),
      stipulations: [],
      declineReasons: parsed?.declineReasons ?? [],
      lenderFees,
      termOptions,
      paymentTiers,
      apr: toNumber(form.apr ?? ''),
      dealerBuyRate: toNumber(form.dealerBuyRate ?? ''),
      customerRate: toNumber(form.customerRate ?? ''),
      residualAmount: toNumber(form.residualAmount ?? ''),
      balloonPayment: toNumber(form.balloonPayment ?? ''),
      totalDueAtSigning: toNumber(form.totalDueAtSigning ?? ''),
      advancePaymentsCount: toInt(form.advancePaymentsCount ?? ''),
      paymentCalculationType: form.paymentCalculationType || null,
      // Deal Details
      typeOfFinancing: form.typeOfFinancing || null,
      programName: form.programName || null,
      invoiceAmount: toNumber(form.invoiceAmount ?? ''),
      salesPrice: toNumber(form.salesPrice ?? ''),
      cashDown: toNumber(form.cashDown ?? ''),
      netTradeIn: toNumber(form.netTradeIn ?? ''),
      taxesAndFees: toNumber(form.taxesAndFees ?? ''),
      dealerCommission: toNumber(form.dealerCommission ?? ''),
      // Parties
      underwriterName: form.underwriterName || null,
      coBorrowerName: form.coBorrowerName || null,
      dealerName: form.dealerName || null,
      guarantorName: form.guarantorName || null,
      // Dates
      decisionDate: form.decisionDate || null,
      expirationDate: form.expirationDate || null,
      firstPaymentDueDate: form.firstPaymentDueDate || null,
      // Program & Terms
      irregularPayments: form.irregularPayments || null,
      // Details
      recourseType: form.recourseType || null,
    };

    await updateParsedDecision({
      variables: { input: { parsedDecisionId, parsedData } },
    });

    onSaveComplete?.();
    onBack();
  };

  // Summary cards data
  const summaryCards: SummaryCard[] = [
    {
      label: 'Approved Amount',
      key: 'approvalAmount',
      icon: <AttachMoneyIcon sx={{ fontSize: 18, color: '#2196f3' }} />,
      format: (v: any) => formatCurrencyShort(toNumber(v ?? '')),
    },
    {
      label: 'Term',
      key: 'termLengthMonths',
      icon: <CalendarTodayIcon sx={{ fontSize: 18, color: '#4caf50' }} />,
      format: (v: any) => formatMonths(toInt(v ?? '')),
    },
    {
      label: 'Monthly Payment',
      key: 'monthlyPayment',
      icon: <ReceiptLongIcon sx={{ fontSize: 18, color: '#2196f3' }} />,
      format: (v: any) => formatCurrency(toNumber(v ?? '')),
    },
    {
      label: 'Rate',
      key: 'interestRate',
      icon: <PercentIcon sx={{ fontSize: 18, color: '#ff9800' }} />,
      format: (v: any) => formatPercentage(toNumber(v ?? '')),
    },
    {
      label: 'Down Payment',
      key: 'downPaymentToVendor',
      icon: <AttachMoneyIcon sx={{ fontSize: 18, color: '#9c27b0' }} />,
      format: (v: any) => formatCurrencyShort(toNumber(v ?? '')),
    },
    {
      label: 'Doc Fees',
      key: 'docFees',
      icon: <AttachMoneyIcon sx={{ fontSize: 18, color: '#2196f3' }} />,
      format: (v: any) => formatCurrencyShort(toNumber(v ?? '')),
    },
    {
      label: 'Conditions',
      key: '_conditions',
      icon: <ListAltIcon sx={{ fontSize: 18, color: '#2196f3' }} />,
      format: () => String(conditions.length),
    },
    {
      label: 'Expiration',
      key: 'expirationDate',
      icon: <LockClock sx={{ fontSize: 18, color: '#f44336' }} />,
      format: (v: any) => v || '—',
    },
  ];

  if (!parsed) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography color="text.secondary">No parsed data available.</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack width="100%" direction="row" alignItems="center" justifyContent="space-between">
        <Button size="small" onClick={onBack} variant="text" startIcon={<ArrowBack />}>
          Back
        </Button>
      </Stack>

      <Typography variant="h5" fontWeight={700} sx={{ mb: 0.5 }}>
        Review Extracted Terms
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Verify each field, click to edit, then confirm to save to submission.
      </Typography>

      {/* Business name & decision badge */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <Typography variant="h6" fontWeight={700}>
          {parsed?.businessName || lenderName}
        </Typography>
        {parsed?.decisionType && (
          <Chip
            label={parsed.decisionType.replace(/_/g, '-').toUpperCase()}
            size="medium"
            sx={{
              fontWeight: 700,
              fontSize: '0.85rem',
              letterSpacing: 0.5,
              ...(/approved/i.test(parsed.decisionType)
                ? { bgcolor: 'success.main', color: '#fff' }
                : /declined/i.test(parsed.decisionType)
                  ? { bgcolor: 'error.main', color: '#fff' }
                  : { bgcolor: 'grey.400', color: '#fff' }),
            }}
          />
        )}
      </Box>

      {/* Summary cards row */}
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        {summaryCards.map(card => {
          const val = card.key === '_conditions' ? String(conditions.length) : (form[card.key] ?? '');
          const status = card.key === '_conditions' ? 'extracted' : getFieldStatus(card.key);
          return (
            <Paper
              key={card.key}
              variant="outlined"
              sx={{
                px: 2,
                py: 1.5,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                minWidth: 110,
                flex: '1 1 0',
                borderColor: status === 'edited' ? 'primary.main' : 'divider',
                borderWidth: status === 'edited' ? 2 : 1,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  {card.label}
                </Typography>
                {card.icon}
              </Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {card.format(val)}
              </Typography>
            </Paper>
          );
        })}
      </Box>

      {/* Two-column layout */}
      <Grid2 container spacing={3}>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <ApprovalDetailsPanel
            form={form}
            originalValues={originalValues}
            getFieldStatus={getFieldStatus}
            getDisplayValue={getDisplayValue}
            onFieldChange={handleFieldChange}
            onSave={handleSaveAll}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <EditableConditionsList
            conditions={conditions}
            onAdd={handleAddCondition}
            onRemove={handleRemoveCondition}
            onChange={handleConditionChange}
          />

          {/* Save button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleSaveAll}
              disabled={saving}
              sx={{ fontWeight: 600 }}
            >
              {saving ? 'Saving…' : 'Save All Changes'}
            </Button>
          </Box>
        </Grid2>
      </Grid2>
    </Box>
  );
};
